import normalize from 'components/Dashboard/utilities/normalizeString';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';

const mapParliamentaryRouteBySlug = (type) => {
  switch (type) {
    case 'scottish-official-report':
      return 'country-specific-parliamentary-record/scotland';
    case 'welsh-official-report':
      return 'country-specific-parliamentary-record/wales';
    case 'welsh-written-q-and-a':
      return 'country-specific-question-and-answer/wales';
    case 'scottish-written-q-and-a':
      return 'country-specific-question-and-answer/scotland';
    case 'northern-irish-written-q-and-a':
      return 'country-specific-question-and-answer/northernireland';
    case 'written-q-and-a':
      return 'question-and-answer';
    case 'edm':
      return 'edm';
    case 'senedd-committee-transcript':
      return 'senedd-committee-transcript';
    case 'crec':
      return 'crec';
    default:
      return 'parliamentaryrecord';
  }
};

const mapParliamentaryRouteByContentType = (contentType) => {
  switch (contentType) {
    case 'QuestionAndAnswer':
      return 'question-and-answer';
    case 'ScotlandCountrySpecificParliamentaryContribution':
      return 'country-specific-parliamentary-record/scotland';
    case 'WalesCountrySpecificParliamentaryContribution':
      return 'country-specific-parliamentary-record/wales';
    case 'ScotlandQuestionAndAnswer':
      return 'country-specific-question-and-answer/scotland';
    case 'WalesQuestionAndAnswer':
      return 'country-specific-question-and-answer/wales';
    case 'NorthernIrelandQuestionAndAnswer':
      return 'country-specific-question-and-answer/northernireland';
    case 'Edm':
      return 'edm';
    case 'SeneddCommitteeTranscriptContribution':
      return 'senedd-committee-transcript';
    default:
      return 'parliamentaryrecord';
  }
};

const normalizeCommonPathname = (pathname) => {
  const characters = pathname.split('');
  if (characters?.[characters?.length - 1] === 's') {
    characters.splice(characters?.length - 1, 1);
  }
  return characters.join('').replace(' ', '').toLowerCase();
};

export const createContentPathname = ({ nameSection, path, propType, propId, routeType, currentPathname }) => {
  const idToUse = propId;
  switch (nameSection) {
    case 'CommitteePublications':
      return `/api/committee-publication/meta-only/${idToUse}`;
    case 'LibraryMaterial':
      return `/api/commons-library-article/${idToUse}`;
    case 'GovernmentPublications':
      return `/api/government-publication/${idToUse}`;
    case 'Regulations':
      return `/api/regulation-docket/${idToUse}`;
    case 'RegulationDocuments':
      return `/api/regulation-document/${idToUse}`;
    case 'RegulationComments':
      return `/api/regulation-document-comment/${idToUse}`;
    case 'Legislation': {
      const typeToUse = propType ?? routeType;
      return `/api/${typeToUse === 'us' ? 'us-' : typeToUse}legislation/${idToUse}`;
    }
    case 'ParliamentaryRecord':
      return `/api/${mapParliamentaryRouteBySlug(propType ?? routeType)}/${idToUse}`;
    case 'Calendar':
      return `/api/event/${propId}`;
    case 'PolicyAsks':
    case 'BriefingMaterial':
    case 'NewsRelease':
    case 'Research':
      return `/api/user-generated-content/${path === 'Policy asks' ? 'policy-ask' : normalize(path)}/${propId}`;
    default:
      return `/api/${normalizeCommonPathname(path)}/${idToUse}`;
  }
};

export const createEmbedContentPathname = ({ item, includeBrandingProperty }) => {
  let routeName;
  switch (item?.type) {
    case 'Legislation':
      routeName = `${item.contentType.toLowerCase()}legislation`;
      break;
    case 'ParliamentaryRecord':
      routeName = mapParliamentaryRouteByContentType(item?.contentType);
      break;
    case 'CommonsLibraryArticle':
      routeName = 'commons-library-article';
      break;
    case 'RegulationDocument':
      routeName = 'regulation-document';
      break;
    default:
      routeName = item?.type?.toLowerCase();
      break;
  }

  const removeRelativeLinksQuery =
    item?.type === 'KeyUpdate' && !includeBrandingProperty ? '?removeRelativeLinks=true' : '';
  return `/api/${routeName}/${item?.id}${removeRelativeLinksQuery}`;
};

export const createSpecificUpdateContentPathname = ({ initialItem }) => {
  const { contentType, id } = initialItem ?? {};
  const type = initialItem?.objectType ?? initialItem?.type;
  const url = initialItem?.contentUrl ?? initialItem?.canonicalUrl;
  const urlType = url?.split('/')?.[2];

  let routeName;
  switch (type) {
    case 'Legislation':
      routeName = `${urlType === 'us' ? 'us-' : urlType?.toLowerCase()}legislation`;
      break;
    case 'CountrySpecificParliamentaryRecord':
    case 'ParliamentaryRecord':
    case 'WrittenAnswer':
    case 'WrittenQuestion':
      routeName = mapParliamentaryRouteBySlug(urlType);
      break;
    case 'SeneddCommitteeTranscript':
      routeName = 'senedd-committee-transcript';
      break;
    case 'UserContent':
    case 'PolicyAsk':
    case 'BriefingMaterial':
    case 'NewsRelease':
    case 'Research':
      routeName = `user-generated-content/${TransformFromPascalCase(contentType ?? initialItem?.type)
        .toLowerCase()
        .replace(' ', '-')}`;
      break;
    case 'CommonsLibraryArticle':
      routeName = 'commons-library-article';
      break;
    case 'CommitteePublication':
      routeName = 'committee-publication';
      break;
    case 'GovernmentPublication':
      routeName = 'government-publication';
      break;
    case 'CrmInteraction':
      routeName = 'crm-interaction';
      break;
    case 'RegulationDocket':
      routeName = 'regulation-docket';
      break;
    case 'RegulationDocument':
      routeName = 'regulation-document';
      break;
    case 'RegulationDocumentComment':
      routeName = 'regulation-document-comment';
      break;
    default:
      routeName = type?.toLowerCase();
      break;
  }

  return `/api/${routeName}/${id}`;
};

export const getTitleHighlightField = ({ type, contentType, urlType }) => {
  switch (type) {
    case 'Legislation':
      return contentType === 'Primary' || urlType === 'Primary' ? 'billTitleHighlighted' : 'titleHighlighted';
    case 'KeyUpdate':
      return 'subjectHighlighted';
    case 'ParliamentaryRecord':
      return contentType?.includes?.('QuestionAndAnswer') || urlType === 'QuestionAndAnswer'
        ? 'questionForHighlighted'
        : 'titleHighlighted';
    default:
      return 'titleHighlighted';
  }
};

export const getBodyHighlightField = ({ type, contentType, urlType, currentNew }) => {
  switch (type) {
    case 'Legislation':
      return contentType === 'Primary' || urlType === 'Primary' ? 'summary' : 'explanatoryNote';
    case 'ParliamentaryRecord':
      if (contentType?.includes?.('QuestionAndAnswer') || urlType === 'QuestionAndAnswer') {
        return 'questionText';
      }
      return currentNew?.content ? 'content' : 'body';
    default:
      return 'body';
  }
};
